import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ProjectDashboard from '../../components/project_dashboard/Components/project_dashboard';
import PortfolioView from '../../components/project_dashboard/Containers/portfolio_view';
import { getHeaders, toastStyle } from '../../components/project_dashboard/Containers/continerUtilityFunctions';
import { toast } from "react-hot-toast";
import { authCheck } from '../../components/project_dashboard/Containers/api';
import { setCsrfToken, setAuthenticated, setHubDomain, setTab, setApiHeader, setApiKey, setIsBeta} from '../../store/slices/globalSlice';
import { setSelectedProjectId, setViewMonthlyCostStatusEditor } from '../../store/slices/projectSlice';

const API_AUTH_HEADER = process.env.REACT_APP_API_AUTH_HEADER;
const API_KEY = process.env.REACT_APP_API_KEY;

// Trust model: the iframe accepts whichever parent passed itself in via
// ?domain=. The browser enforces postMessage on both ends — sends are
// rejected if the live parent origin doesn't match targetOrigin, and
// event.origin on receive is set by the browser, not the message sender.
// Who is *allowed* to embed us is enforced by CSP frame-ancestors at the
// HTTP layer (see public/serve.json).
const getParentOriginFromQuery = () => {
	const domain = new URLSearchParams(window.location.search).get('domain');
	if (!domain) return null;
	const protocol = domain.startsWith('localhost') ? 'http' : 'https';
	return `${protocol}://${domain}`;
};

const useReduxSelectors = () => {
	const csrfToken = useSelector((state) => state.global.csrfToken);
	const authenticated = useSelector((state) => state.global.authenticated);
	const tab = useSelector((state) => state.global.tab);
	const hubDomain = useSelector((state) => state.global.hubDomain);
	const isLoading = useSelector((state) => state.global.isLoading);
	const selectedProjectId = useSelector((state) => state.project.selectedProjectId);

	return { csrfToken, authenticated, tab, hubDomain, isLoading, selectedProjectId };
};

const PHD = () => {
	const dispatch = useDispatch();
	const { csrfToken, authenticated, tab, hubDomain, selectedProjectId } = useReduxSelectors();
	const parentOrigin = useMemo(() => getParentOriginFromQuery(), []);

	useEffect(() => {
		dispatch(setApiHeader(API_AUTH_HEADER));
		dispatch(setApiKey(API_KEY));
	}, [dispatch]);

	// Send first handshake to the parent origin from ?domain=. targetOrigin
	// makes the browser drop the message if the live parent isn't this origin.
	useEffect(() => {
		if (!parentOrigin) return;
		dispatch(setHubDomain(parentOrigin));
		window.parent.postMessage(
			{ origin: window.location.origin, type: 'first_hand_shake' },
			parentOrigin,
		);
	}, [parentOrigin, dispatch]);

	useEffect(() => {
		const messageListener = (event) => {
			if (!parentOrigin || event.origin !== parentOrigin) return;
			const localTab = event.data.tab === 'None' ? 'project' : event.data.tab;
			let localProject = null;
			try {
				localProject = parseInt(event.data.project_id);
			} catch (error) {
				localProject = null;
			}
			dispatch(setTab(localTab));
			dispatch(setSelectedProjectId(localProject));
			dispatch(setHubDomain(event.data.current_domain));
			dispatch(setCsrfToken(event.data.token));
			dispatch(setViewMonthlyCostStatusEditor(event.data?.viewMonthlyCostStatusEditor === "True" ? true : false));
		};

		window.addEventListener("message", messageListener);

		return () => {
			window.removeEventListener("message", messageListener);
		};
	}, [dispatch, parentOrigin]);

	// Send message to parent for tab and project change
	useEffect(() => {
		if (hubDomain) {
			const data = {
				type: 'params_change',
				origin: window.location.origin,
				tab: tab,
				project_id: selectedProjectId,
			};
			window.parent.postMessage(data, hubDomain);
		}		
	}, [tab,selectedProjectId,hubDomain]);

	useEffect(() => {
		if (csrfToken) {
			const urlParams = new URLSearchParams(window.location.search);
			const userUid = urlParams.get('user_uid');

			const headers = getHeaders(csrfToken, API_AUTH_HEADER, API_KEY);
			const authCheckAsync = async () => {
				try {
					const authData = await authCheck(userUid, csrfToken, headers, hubDomain);
					if (authData?.auth_check_passed) {
						dispatch(setAuthenticated(true));
						dispatch(setIsBeta(authData?.is_beta));
					} else {
						console.error('Auth Check #1 Failed', authData);
					}
				} catch (error) {
					console.error('Error during auth check:', error);
					toast.error(error.message || "Authentication failed. Please reload and try again.", toastStyle());
				}
			};

			authCheckAsync();
		}
	}, [csrfToken, dispatch, hubDomain]);
				
	if (!authenticated) {
		return <div>Loading...</div>;
	}

	return (
		<div>
			{tab === "project" ? (
				<ProjectDashboard />
			) : tab === 'portfolio' ? (
				<PortfolioView />
			) : null}
		</div>
	);
};

export default PHD;